import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ACAO_AUDITORIA_ROTULO, ACOES_AUDITORIA, formatarDataHoraSegundos, type AcaoAuditoria } from "@caixaflow/shared";
import { api, qs } from "../../api/cliente";
import type { Pagina, RegistroAuditoria } from "../../api/tipos";
import { Campo } from "../../components/Campos";
import { Carregando, FalhaAoCarregar } from "../../components/Estados";

type Resposta = Pagina<RegistroAuditoria> & { usuarios: { id: string; nome: string }[] };

const ACOES_DE_ALERTA = new Set(["LOGIN_FALHA", "AUTORIZACAO_ADMIN_FALHA", "VENDA_CANCELADA", "CAIXA_REABERTO", "VENDA_DIVERGENCIA_AUTORIZADA"]);

/** Detalhes em uma linha legível: "nome: João → João Silva · perfil: OPERADOR → ADMIN". */
function resumirDetalhes(detalhes: unknown): string {
  if (!detalhes || typeof detalhes !== "object") return "";
  return Object.entries(detalhes as Record<string, unknown>)
    .map(([chave, valor]) => {
      if (valor && typeof valor === "object" && "de" in valor && "para" in valor) {
        const v = valor as { de: unknown; para: unknown };
        return `${chave}: ${String(v.de ?? "—")} → ${String(v.para ?? "—")}`;
      }
      if (Array.isArray(valor)) return `${chave}: ${valor.length > 3 ? `${valor.length} itens` : valor.join(", ")}`;
      if (valor && typeof valor === "object") return `${chave}: ${JSON.stringify(valor)}`;
      return `${chave}: ${String(valor)}`;
    })
    .join(" · ");
}

export function PaginaAuditoria() {
  const [acao, setAcao] = useState("");
  const [usuarioId, setUsuarioId] = useState("");
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");
  const [pagina, setPagina] = useState(1);
  const filtrar = <T,>(setter: (v: T) => void) => (v: T) => {
    setPagina(1);
    setter(v);
  };

  const consulta = useQuery({
    queryKey: ["auditoria", acao, usuarioId, inicio, fim, pagina],
    queryFn: () => api<Resposta>(`/auditoria${qs({ acao, usuarioId, inicio, fim, pagina, porPagina: 50 })}`),
    placeholderData: keepPreviousData,
  });
  const d = consulta.data;
  const totalPaginas = d ? Math.max(1, Math.ceil(d.total / d.porPagina)) : 1;

  return (
    <>
      <section className="card pad">
        <div className="grade-filtros">
          <Campo rotulo="Ação">
            <select value={acao} onChange={(e) => filtrar(setAcao)(e.target.value)}>
              <option value="">Todas</option>
              {ACOES_AUDITORIA.map((a) => (
                <option key={a} value={a}>
                  {ACAO_AUDITORIA_ROTULO[a]}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Usuário">
            <select value={usuarioId} onChange={(e) => filtrar(setUsuarioId)(e.target.value)}>
              <option value="">Todos</option>
              {d?.usuarios.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nome}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="De">
            <input type="datetime-local" value={inicio} onChange={(e) => filtrar(setInicio)(e.target.value)} />
          </Campo>
          <Campo rotulo="Até">
            <input type="datetime-local" value={fim} min={inicio} onChange={(e) => filtrar(setFim)(e.target.value)} />
          </Campo>
        </div>
      </section>

      {consulta.isPending && <Carregando />}
      {consulta.error && <FalhaAoCarregar erro={consulta.error} tentarDeNovo={() => consulta.refetch()} />}
      {d && (
        <div className="card tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Quando</th>
                <th>Ação</th>
                <th>Usuário</th>
                <th>Detalhes</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {d.itens.length === 0 && (
                <tr>
                  <td colSpan={5} className="vazio">
                    Nenhum registro nestes filtros.
                  </td>
                </tr>
              )}
              {d.itens.map((r) => (
                <tr key={r.id}>
                  <td className="mudo">{formatarDataHoraSegundos(r.em)}</td>
                  <td>
                    <span className={`chip ${ACOES_DE_ALERTA.has(r.acao) ? "alerta" : "neutro"}`}>
                      {ACAO_AUDITORIA_ROTULO[r.acao as AcaoAuditoria] ?? r.acao}
                    </span>
                  </td>
                  <td>{r.usuario?.nome ?? <span className="mudo">—</span>}</td>
                  <td className="quebra pequeno" style={{ maxWidth: 520 }} title={JSON.stringify(r.detalhes)}>
                    {resumirDetalhes(r.detalhes) || <span className="mudo">—</span>}
                  </td>
                  <td className="mudo pequeno">{r.ip ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {totalPaginas > 1 && (
        <div className="linha">
          <button className="btn pequeno fantasma" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
            Mais recentes
          </button>
          <span className="mudo pequeno">
            Página {pagina} de {totalPaginas} · {d?.total} registros
          </span>
          <button className="btn pequeno fantasma" disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)}>
            Anteriores
          </button>
        </div>
      )}
    </>
  );
}
